<script setup lang="ts">
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'reka-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import AppIcon from '@/components/common/AppIcon.vue'
import { useServerSelectionStore } from '@/stores/serverSelection'
import { useUserStore } from '@/stores/user'

/**
 * Picks one of the user's favourite servers. The "+" jumps to the profile,
 * which is where favourites are managed.
 */
const { t } = useI18n()
const user = useUserStore()
const selection = useServerSelectionStore()
const router = useRouter()

// I mondi conclusi vanno all'archivio, non qui: restano preferiti in DB, ma
// vanno trattati come se non lo fossero in ogni superficie "corrente".
const favorites = computed(() => user.favoriteServers.filter((server) => !server.closed))

function goToFavourites(): void {
  void router.push({ name: 'profile', hash: '#favorite-servers' })
}
</script>

<template>
  <div class="flex min-w-0 items-center gap-1">
    <!-- No favourites yet: only the shortcut to add some. -->
    <div
      v-if="favorites.length === 0"
      class="text-parchment-300 hidden min-w-0 items-center gap-2 rounded-md border border-white/10 px-2.5 py-1.5 text-sm md:flex"
    >
      <AppIcon name="mdi-earth-off" class="shrink-0 text-base opacity-70" />
      <span class="truncate">{{ t('header.server.none') }}</span>
    </div>

    <DropdownMenuRoot v-else>
      <DropdownMenuTrigger
        class="chrome-item min-w-0 cursor-pointer gap-2 border border-white/10 bg-black/20 px-2.5"
        :aria-label="t('common.fields.server')"
      >
        <AppIcon name="mdi-earth" class="shrink-0 text-gold-300 text-base" />
        <span class="max-w-32 truncate font-semibold">
          {{ selection.selected?.server_name ?? t('header.server.none') }}
        </span>
        <AppIcon name="mdi-chevron-down" class="text-base opacity-70" />
      </DropdownMenuTrigger>

      <DropdownMenuPortal>
        <DropdownMenuContent
          :side-offset="10"
          align="end"
          class="panel z-50 max-h-80 min-w-56 overflow-y-auto p-1 shadow-raised"
        >
          <DropdownMenuLabel class="text-abyss-800/70 px-2 py-1 text-xs font-semibold uppercase">
            {{ t('common.fields.server') }}
          </DropdownMenuLabel>
          <DropdownMenuSeparator class="bg-parchment-300 my-1 h-px" />

          <DropdownMenuItem
            v-for="server in favorites"
            :key="server.id"
            class="text-abyss-900 flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm outline-none data-highlighted:bg-primary-50"
            @select="selection.select(server.id)"
          >
            <span class="flex-1 truncate">{{ server.server_name }}</span>
            <span v-if="server.speed" class="text-abyss-800/60 text-xs">x{{ server.speed }}</span>
            <AppIcon
              v-if="selection.selected?.id === server.id"
              name="mdi-check"
              class="text-primary-600 shrink-0"
            />
          </DropdownMenuItem>

          <DropdownMenuSeparator class="bg-parchment-300 my-1 h-px" />
          <DropdownMenuItem
            class="text-primary-800 flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm font-semibold outline-none data-highlighted:bg-primary-50"
            @select="goToFavourites"
          >
            <AppIcon name="mdi-plus" />
            <span>{{ t('header.server.manage') }}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenuRoot>

    <!-- Always-visible "+" so adding a server is one click away. -->
    <button
      type="button"
      class="chrome-item cursor-pointer border border-white/10 bg-black/20 px-2"
      :aria-label="t('header.server.add')"
      :title="t('header.server.add')"
      @click="goToFavourites"
    >
      <AppIcon name="mdi-plus" class="text-base" />
    </button>
  </div>
</template>
